import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  CloudArrowUp,
  FileText,
  CircleNotch,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  fetchMyMatters,
  requestDocumentUploadUrl,
  confirmDocumentUpload,
} from '@/lib/solicitorPortalApi';
import { getApiErrorMessage } from '@/lib/api';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  matterId?: string;
  onClose: () => void;
  onUploaded: () => void;
}

const ALLOWED_MIME = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/rtf',
  'text/plain',
  'image/jpeg',
  'image/png',
  'image/heic',
]);
const MAX_BYTES = 50 * 1024 * 1024;

type Stage = 'idle' | 'requesting' | 'uploading' | 'confirming' | 'done';

export function SolicitorUploadModal({ matterId, onClose, onUploaded }: Props) {
  const qc = useQueryClient();
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(matterId ?? null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stage, setStage] = useState<Stage>('idle');
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const mattersQ = useQuery({
    queryKey: ['portal', 'matters'],
    queryFn: fetchMyMatters,
    enabled: !matterId, // Skip the fetch when caller pinned the matter.
  });

  // Auto-fill the document name from the file's own name on first pick.
  useEffect(() => {
    if (file && !name) {
      const stem = file.name.replace(/\.[^.]+$/, '');
      setName(stem);
    }
  }, [file, name]);

  const handleFileChosen = (f: File | null) => {
    if (!f) return;
    if (!ALLOWED_MIME.has(f.type)) {
      toast.error('That file type is not supported. Please use PDF, Word, Excel, PowerPoint, RTF, plain text, or an image.');
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error('That file is over the 50MB limit. Please split or compress it.');
      return;
    }
    setFile(f);
    setProgress(0);
  };

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected');
      if (!selectedMatterId) throw new Error('Please choose a matter');
      if (!name.trim()) throw new Error('Please give the document a name');

      // 1. Ask the server for a presigned PUT URL.
      setStage('requesting');
      const { uploadUrl, fileKey } = await requestDocumentUploadUrl({
        matterId: selectedMatterId,
        fileName: file.name,
        mimeType: file.type,
        fileSizeBytes: file.size,
      });

      // 2. PUT the file straight to S3 with a progress callback so the
      //    bar moves while a large brief uploads.
      setStage('uploading');
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
        // Strip the default api auth headers — S3 hates them.
        transformRequest: (data) => data,
      });

      // 3. Tell the server we're done. Server HEADs the object to verify
      //    it landed before creating a Document row.
      setStage('confirming');
      await confirmDocumentUpload({
        matterId: selectedMatterId,
        fileKey,
        name: name.trim(),
        description: description.trim() || undefined,
      });

      setStage('done');
    },
    onSuccess: () => {
      toast.success('Document uploaded — chambers staff have been notified.');
      qc.invalidateQueries({ queryKey: ['portal', 'documents'] });
      onUploaded();
    },
    onError: (err) => {
      setStage('idle');
      setProgress(0);
      toast.error(getApiErrorMessage(err, 'Upload failed. Please try again.'));
    },
  });

  const isBusy = stage !== 'idle' && stage !== 'done';
  const stageLabel: Record<Stage, string> = {
    idle: '',
    requesting: 'Preparing secure upload…',
    uploading: `Uploading ${progress}%…`,
    confirming: 'Finalising…',
    done: 'Done!',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isBusy) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold tracking-tight">Upload document</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Sent securely to chambers — they'll be notified the moment it lands.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !isBusy && onClose()}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Matter selector */}
          {!matterId && (
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Matter
              </label>
              <select
                value={selectedMatterId ?? ''}
                onChange={(e) => setSelectedMatterId(e.target.value || null)}
                disabled={isBusy}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-600"
              >
                <option value="">— Select a matter —</option>
                {mattersQ.data?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.caseNumber} · {m.caseName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* File picker */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              File
            </label>
            <input
              ref={fileInputRef}
              type="file"
              className="sr-only"
              onChange={(e) => handleFileChosen(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2.5">
                <FileText size={20} className="shrink-0 text-brand-600 dark:text-brand-400" weight="duotone" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{file.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {(file.size / 1024 / 1024).toFixed(2)} MB · {file.type || 'unknown type'}
                  </div>
                </div>
                {!isBusy && (
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-xs font-medium text-red-600 hover:text-red-700 dark:hover:text-red-300 dark:text-red-300"
                  >
                    Change
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isBusy}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground transition hover:border-brand-600/50 hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <CloudArrowUp size={28} weight="duotone" className="text-brand-600 dark:text-brand-400/80" />
                <div>
                  <div className="font-medium text-foreground">Choose a file</div>
                  <div className="mt-0.5 text-xs">PDF, Word, Excel, PowerPoint, image — up to 50MB</div>
                </div>
              </button>
            )}
          </div>

          {/* Name + description */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Brief — Smith v Jones"
              disabled={isBusy}
              maxLength={255}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Description <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
            </label>
            <ExpandableTextarea
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder="A short note — e.g. 'first-instance brief, hearing 12 May'."
              disabled={isBusy}
              maxLength={1000}
              rows={2}
            />
          </div>

          {/* Progress bar */}
          {isBusy && (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-xs">
              <div className="mb-1.5 flex items-center gap-2 font-medium">
                <CircleNotch size={14} className="animate-spin text-brand-600 dark:text-brand-400" />
                {stageLabel[stage]}
              </div>
              {stage === 'uploading' && (
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-brand-600 transition-[width] duration-200"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => uploadMut.mutate()}
            disabled={!file || !selectedMatterId || !name.trim() || isBusy}
            className="gap-1.5"
          >
            {isBusy ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <CloudArrowUp size={14} weight="fill" />
            )}
            Upload
          </Button>
        </div>
      </Card>
    </div>
  );
}
